import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Loader2,
  StickyNote,
  Lock,
  LinkIcon,
  Paperclip,
  Plus,
  X,
  Download,
  Trash2,
  CalendarOff,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  plannerAccess,
  listPlannerEvents,
  updatePlannerEvent,
  uploadPlannerAttachment,
  deletePlannerAttachment,
  attachmentUrl,
} from "../lib/api";

const parseISO = (s) => {
  const [y, m, d] = (s || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const fmtLong = (d) =>
  d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

const prettySize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function PlannerPaDay() {
  const { date } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const saveTimer = useRef(null);

  const [access, setAccess] = useState({ has_access: false, loading: true });
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null); // the pa_day PlannerEvent
  const [notes, setNotes] = useState("");
  const [savedNotes, setSavedNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [links, setLinks] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const dateObj = useMemo(() => parseISO(date), [date]);

  useEffect(() => {
    plannerAccess()
      .then((res) => setAccess({ ...res, loading: false }))
      .catch(() => setAccess({ has_access: false, loading: false }));
  }, []);

  useEffect(() => {
    if (!access.has_access) return;
    setLoading(true);
    listPlannerEvents(date, date)
      .then((list) => {
        const pa = (list || []).find((e) => e.kind === "pa_day" && !e.period_label);
        setEvent(pa || null);
        setNotes(pa?.notes || "");
        setSavedNotes(pa?.notes || "");
        setLinks(Array.isArray(pa?.links) ? pa.links : []);
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [access.has_access, date]);

  // Debounced notes auto-save.
  useEffect(() => {
    if (loading || !event) return;
    if (notes === savedNotes) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSavingNotes(true);
      try {
        await updatePlannerEvent(event.id, { notes });
        setSavedNotes(notes);
      } catch {
        toast.error("Could not save notes.");
      } finally {
        setSavingNotes(false);
      }
    }, 700);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [notes, savedNotes, event, loading]);

  const goToDay = (delta) => navigate(`/planner/pa-day/${isoDate(addDays(dateObj, delta))}`);

  const persistLinks = async (next) => {
    setLinks(next);
    try {
      await updatePlannerEvent(event.id, { links: next });
    } catch {
      toast.error("Could not save link.");
    }
  };

  const addLink = () => {
    const url = linkUrl.trim();
    if (!url) return;
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const next = [...links, { url: normalized, label: linkLabel.trim() }];
    persistLinks(next);
    setLinkUrl("");
    setLinkLabel("");
  };

  const removeLink = (idx) => persistLinks(links.filter((_, i) => i !== idx));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !event) return;
    setUploading(true);
    setProgress(0);
    try {
      const updated = await uploadPlannerAttachment(event.id, file, setProgress);
      setEvent(updated);
      toast.success("Attachment added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = async (attachmentId) => {
    try {
      await deletePlannerAttachment(event.id, attachmentId);
      setEvent((ev) => ({ ...ev, attachments: (ev.attachments || []).filter((a) => a.id !== attachmentId) }));
      toast.success("Attachment removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove.");
    }
  };

  if (access.loading) {
    return <p className="text-[#4a6984]/70 p-6" data-testid="paday-loading">Loading…</p>;
  }
  if (!access.has_access) {
    return (
      <div className="ae-card p-10 text-center max-w-xl mx-auto mt-8" data-testid="paday-upsell">
        <Lock className="h-9 w-9 mx-auto text-[#4a6984]/70 mb-3" />
        <h3 className="font-extrabold text-xl text-[#1c2d3d] mb-2">Planner is a Pro feature</h3>
        <Link to="/pricing">
          <Button className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold">Upgrade to Pro</Button>
        </Link>
      </div>
    );
  }

  const attachments = event?.attachments || [];

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4" data-testid="paday-root">
      <div className="flex items-center justify-between gap-2">
        <Link
          to={`/dashboard?view=planner&date=${date}`}
          className="text-sm font-semibold text-[#4a6984] hover:text-[#1c2d3d] inline-flex items-center gap-1"
          data-testid="paday-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to month
        </Link>
        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => goToDay(-1)} data-testid="paday-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => goToDay(1)} data-testid="paday-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="ae-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-[#e6f6ec] text-[#2e7d4f] border border-[#bfe6cd]">
            <CalendarOff className="h-3.5 w-3.5" /> PA Day
          </span>
        </div>
        <h2 className="font-extrabold text-2xl text-[#1c2d3d]" data-testid="paday-date">{fmtLong(dateObj)}</h2>
        {event?.activity_name && event.activity_name !== "PA Day" && (
          <p className="text-sm text-[#4a6984] mt-0.5">{event.activity_name}</p>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-[#4a6984] py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !event ? (
          <div className="py-8 text-center text-[#4a6984]" data-testid="paday-empty">
            <p className="font-semibold text-[#1c2d3d]">No PA day is marked on this date.</p>
            <p className="text-sm mt-1">Add it from the planner&apos;s “Days off” button, then it&apos;ll open here.</p>
          </div>
        ) : (
          <div className="mt-5 space-y-7">
            {/* Notes */}
            <section data-testid="paday-notes-section">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#4a6984] flex items-center gap-1.5 mb-2">
                <StickyNote className="h-3.5 w-3.5" /> Notes
              </h3>
              <Textarea
                rows={8}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={"e.g.\n• PD session on assessment in the library, 9–11\n• Bring laptop + report card samples"}
                className="text-sm leading-relaxed"
                data-testid="paday-notes-textarea"
              />
              <p className="text-[11px] text-[#4a6984]/60 mt-1 h-4" data-testid="paday-notes-status">
                {savingNotes ? "Saving…" : notes === savedNotes ? "All changes saved." : ""}
              </p>
            </section>

            {/* Links */}
            <section data-testid="paday-links-section">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#4a6984] flex items-center gap-1.5 mb-2">
                <LinkIcon className="h-3.5 w-3.5" /> Links
              </h3>
              <div className="space-y-1.5 mb-3">
                {links.length === 0 && (
                  <p className="text-xs text-[#4a6984]/60 italic">No links yet.</p>
                )}
                {links.map((l, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 rounded-md border border-[#4a6984]/12 bg-white px-3 py-2"
                    data-testid={`paday-link-${idx}`}
                  >
                    <LinkIcon className="h-3.5 w-3.5 text-[#4a6984]/60 shrink-0" />
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#1c2d3d] hover:text-[#1c9e7e] font-semibold truncate flex-1"
                    >
                      {l.label || l.url}
                    </a>
                    <button
                      type="button"
                      onClick={() => removeLink(idx)}
                      className="text-[#c45a6b] hover:bg-[#c45a6b]/5 rounded p-1"
                      aria-label="Remove link"
                      data-testid={`paday-link-remove-${idx}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                <Input
                  placeholder="https://…"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  data-testid="paday-link-url"
                />
                <Input
                  placeholder="Optional label"
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  data-testid="paday-link-label"
                />
                <Button
                  onClick={addLink}
                  disabled={!linkUrl.trim()}
                  className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white"
                  data-testid="paday-link-add"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
            </section>

            {/* Attachments */}
            <section data-testid="paday-attachments-section">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#4a6984] flex items-center gap-1.5 mb-2">
                <Paperclip className="h-3.5 w-3.5" /> Attachments
              </h3>
              <div className="space-y-1.5 mb-3">
                {attachments.length === 0 && (
                  <p className="text-xs text-[#4a6984]/60 italic">No files attached yet.</p>
                )}
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 rounded-md border border-[#4a6984]/12 bg-white px-3 py-2"
                    data-testid={`paday-attachment-${a.id}`}
                  >
                    <Paperclip className="h-3.5 w-3.5 text-[#4a6984]/60 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1c2d3d] font-semibold truncate">{a.original_filename}</div>
                      <div className="text-[11px] text-[#4a6984]/60">{prettySize(a.size)}</div>
                    </div>
                    <a
                      href={attachmentUrl(event.id, a.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1c9e7e] hover:bg-[#96ded1]/15 rounded p-1"
                      title="Download"
                      data-testid={`paday-attachment-download-${a.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </a>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.id)}
                      className="text-[#c45a6b] hover:bg-[#c45a6b]/5 rounded p-1"
                      aria-label="Remove attachment"
                      data-testid={`paday-attachment-remove-${a.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={handleUpload}
                data-testid="paday-file-input"
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="paday-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading… {progress}%
                  </>
                ) : (
                  <>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add attachment
                  </>
                )}
              </Button>
              <p className="text-[11px] text-[#4a6984]/60 mt-1">PDFs, images, Word docs — up to 25 MB each.</p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
